import { ISSUER, PORTAL_CLIENT_ID, REDIRECT_URI, SCOPES } from "./config";

const SESSION_KEY = "sonox-im-platform-session";
const PKCE_KEY = "sonox-im-platform-pkce";

export interface Developer {
  readonly userId: string;
  readonly name: string;
  readonly email: string | null;
  readonly avatarUrl: string | null;
}

export interface Session {
  readonly accessToken: string;
  readonly refreshToken?: string;
  readonly expiresAt: number;
  readonly user: Developer | null;
}

interface PkcePending {
  readonly state: string;
  readonly nonce: string;
  readonly verifier: string;
}

export function loadSession(): Session | null {
  try {
    const value = sessionStorage.getItem(SESSION_KEY);
    if (value === null) return null;
    const session = JSON.parse(value) as Session;
    return session.accessToken.length > 0 ? session : null;
  } catch {
    return null;
  }
}

export function saveSession(session: Session | null): void {
  if (session === null) sessionStorage.removeItem(SESSION_KEY);
  else sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export async function startLogin(): Promise<void> {
  const pending: PkcePending = {
    state: randomString(24),
    nonce: randomString(24),
    verifier: randomString(64),
  };
  sessionStorage.setItem(PKCE_KEY, JSON.stringify(pending));
  const challenge = await sha256Base64Url(pending.verifier);
  const url = new URL("/oauth/authorize", ISSUER);
  url.searchParams.set("client_id", PORTAL_CLIENT_ID);
  url.searchParams.set("redirect_uri", REDIRECT_URI);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", SCOPES.join(" "));
  url.searchParams.set("state", pending.state);
  url.searchParams.set("nonce", pending.nonce);
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("code_challenge_method", "S256");
  window.location.assign(url.toString());
}

/** Registers or signs in a first-party Sonox developer account for the portal audience. */
export async function authenticateWithPassword(input: {
  readonly mode: "login" | "register";
  readonly email: string;
  readonly password: string;
  readonly name?: string;
}): Promise<Session> {
  const response = await fetch(new URL(`/auth/${input.mode}`, ISSUER), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      client_id: PORTAL_CLIENT_ID,
      email: input.email,
      password: input.password,
      ...(input.mode === "register" ? { name: input.name } : {}),
    }),
  });
  if (!response.ok) {
    let message = `账号认证失败 (${response.status})`;
    try {
      const body = await response.json() as { readonly error_description?: string };
      message = body.error_description ?? message;
    } catch {
      // Preserve the generic bounded message when an upstream body is invalid.
    }
    throw new Error(message);
  }
  const session = tokenSession(await response.json() as TokenResponse, null);
  saveSession(session);
  return session;
}

export async function completeLogin(): Promise<Session> {
  const query = new URLSearchParams(window.location.search);
  const oauthError = query.get("error");
  if (oauthError !== null) throw new Error(query.get("error_description") ?? oauthError);
  const code = query.get("code");
  const state = query.get("state");
  const rawPending = sessionStorage.getItem(PKCE_KEY);
  sessionStorage.removeItem(PKCE_KEY);
  if (code === null || state === null || rawPending === null) throw new Error("登录状态已过期，请重试");
  const pending = JSON.parse(rawPending) as PkcePending;
  if (state !== pending.state) throw new Error("登录 state 校验失败");
  const tokens = await exchange(new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: REDIRECT_URI,
    client_id: PORTAL_CLIENT_ID,
    code_verifier: pending.verifier,
  }));
  const session = tokenSession(tokens, null);
  saveSession(session);
  return session;
}

export async function freshSession(session: Session): Promise<Session> {
  if (session.expiresAt - Date.now() > 60_000) return session;
  if (session.refreshToken === undefined) throw new Error("登录已过期，请重新登录");
  const tokens = await exchange(new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: session.refreshToken,
    client_id: PORTAL_CLIENT_ID,
  }));
  const refreshed = tokenSession(tokens, session.user, session.refreshToken);
  saveSession(refreshed);
  return refreshed;
}

export async function logout(session: Session | null): Promise<void> {
  if (session?.refreshToken !== undefined) {
    try {
      await fetch(new URL("/oauth/revoke", ISSUER), {
        method: "POST",
        headers: { "content-type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ token: session.refreshToken, client_id: PORTAL_CLIENT_ID }),
      });
    } catch {
      // Revocation is best effort; local credential removal always completes.
    }
  }
  saveSession(null);
}

interface TokenResponse {
  readonly access_token: string;
  readonly refresh_token?: string;
  readonly expires_in: number;
}

async function exchange(body: URLSearchParams): Promise<TokenResponse> {
  const response = await fetch(new URL("/oauth/token", ISSUER), {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!response.ok) throw new Error(`换取 Token 失败 (${response.status})`);
  return response.json() as Promise<TokenResponse>;
}

function tokenSession(tokens: TokenResponse, user: Developer | null, fallbackRefresh?: string): Session {
  return {
    accessToken: tokens.access_token,
    ...(tokens.refresh_token === undefined && fallbackRefresh === undefined
      ? {}
      : { refreshToken: tokens.refresh_token ?? fallbackRefresh }),
    expiresAt: Date.now() + tokens.expires_in * 1_000,
    user,
  };
}

function randomString(length: number): string {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return [...bytes].map((byte) => alphabet[byte % alphabet.length] ?? "A").join("");
}

async function sha256Base64Url(value: string): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value)));
  let binary = "";
  for (const byte of digest) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}
